import { GetIssueResponseDtoSeverityEnum } from '@issuemanager/api-client';
import {
  useAtlasCoreThemeTokens,
  useAtlasSemanticThemeTokens,
} from '@issuemanager/apps/client/theme';
import { useFeatureFlags } from '@issuemanager/client/features/users';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSeverityLocalizer } from './use-localized-severities';

export interface IssueSeverityData {
  id: string;
  value: GetIssueResponseDtoSeverityEnum | null;
  label: string;
  color: string;
  backgroundColor: string;
}

export interface IssueSeverityDataWithoutNullValue
  extends Omit<IssueSeverityData, 'value'> {
  value: GetIssueResponseDtoSeverityEnum;
}

export function useIssueSeverities(includeNotSet = true): IssueSeverityData[] {
  const { t } = useTranslation('issue');
  const { getSeverityLabel } = useSeverityLocalizer();
  const semantic = useAtlasSemanticThemeTokens();
  const core = useAtlasCoreThemeTokens();
  const useNewSeverityValues = useFeatureFlags('useNewSeverityValues');

  const whiteColor = core.color.legacy.white.value;
  const darkColor = semantic.color.text.body.value;

  const issueStatuses: IssueSeverityData[] = useMemo(
    () =>
      [
        {
          id: '1',
          value: GetIssueResponseDtoSeverityEnum.VeryLow,
          label:
            getSeverityLabel(GetIssueResponseDtoSeverityEnum.VeryLow) ??
            GetIssueResponseDtoSeverityEnum.VeryLow,
          color: whiteColor,
          backgroundColor: getSeverityColor(
            GetIssueResponseDtoSeverityEnum.VeryLow,
            semantic,
          ),
        },
        {
          id: '2',
          value: GetIssueResponseDtoSeverityEnum.Low,
          label:
            getSeverityLabel(GetIssueResponseDtoSeverityEnum.Low) ??
            GetIssueResponseDtoSeverityEnum.Low,
          color: darkColor,
          backgroundColor: getSeverityColor(
            GetIssueResponseDtoSeverityEnum.Low,
            semantic,
          ),
        },
        {
          id: '3',
          value: GetIssueResponseDtoSeverityEnum.Medium,
          label:
            getSeverityLabel(GetIssueResponseDtoSeverityEnum.Medium) ??
            GetIssueResponseDtoSeverityEnum.Medium,
          color: darkColor,
          backgroundColor: getSeverityColor(
            GetIssueResponseDtoSeverityEnum.Medium,
            semantic,
          ),
        },
        {
          id: '4',
          value: GetIssueResponseDtoSeverityEnum.High,
          label:
            getSeverityLabel(GetIssueResponseDtoSeverityEnum.High) ??
            GetIssueResponseDtoSeverityEnum.High,
          color: darkColor,
          backgroundColor: getSeverityColor(
            GetIssueResponseDtoSeverityEnum.High,
            semantic,
          ),
        },
        {
          id: '5',
          value: GetIssueResponseDtoSeverityEnum.VeryHigh,
          label:
            getSeverityLabel(GetIssueResponseDtoSeverityEnum.VeryHigh) ??
            GetIssueResponseDtoSeverityEnum.VeryHigh,
          color: whiteColor,
          backgroundColor: getSeverityColor(
            GetIssueResponseDtoSeverityEnum.VeryHigh,
            semantic,
          ),
        },
        {
          id: '6',
          value: GetIssueResponseDtoSeverityEnum.Critical,
          label:
            getSeverityLabel(GetIssueResponseDtoSeverityEnum.Critical) ??
            GetIssueResponseDtoSeverityEnum.Critical,
          color: whiteColor,
          backgroundColor: getSeverityColor(
            GetIssueResponseDtoSeverityEnum.Critical,
            semantic,
          ),
        },
        ...(includeNotSet
          ? [
              {
                id: '7',
                value: null,
                label: t('statistics.severity.notSet'),
                color: darkColor,
                backgroundColor: getSeverityColor(null, semantic),
              },
            ]
          : []),
      ].filter((severity) => {
        if (severity.value === null) return includeNotSet;
        if (useNewSeverityValues) {
          return severity.value !== GetIssueResponseDtoSeverityEnum.Critical;
        }
        return (
          severity.value !== GetIssueResponseDtoSeverityEnum.VeryLow &&
          severity.value !== GetIssueResponseDtoSeverityEnum.VeryHigh
        );
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getSeverityLabel, useNewSeverityValues, includeNotSet],
  );

  return issueStatuses;
}

function getSeverityColor(
  severity: GetIssueResponseDtoSeverityEnum | null,
  semantic: ReturnType<typeof useAtlasSemanticThemeTokens>,
): string {
  switch (severity) {
    case GetIssueResponseDtoSeverityEnum.VeryHigh:
    case GetIssueResponseDtoSeverityEnum.Critical:
      return '#ED8584';
    case GetIssueResponseDtoSeverityEnum.High:
      return semantic.color.accent['03'].value;
    case GetIssueResponseDtoSeverityEnum.Medium:
      return semantic.color.accent['04'].value;
    case GetIssueResponseDtoSeverityEnum.Low:
      return semantic.color.accent['02'].value;
    case GetIssueResponseDtoSeverityEnum.VeryLow:
      return '#88BBA9';
    case null:
      return semantic.color.action.disabled.value;
    default:
      return semantic.color.action.disabled.value;
  }
}
